package com.example.mystybag.communication

import com.example.mystybag.communication.extensions.configure
import com.example.mystybag.communication.internal.commLog
import com.example.mystybag.sharedmodels.base.BaseRequestModel
import com.example.mystybag.sharedmodels.base.BaseResponseModel
import io.ktor.client.HttpClient
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.logging.Logger
import io.ktor.client.plugins.logging.Logging
import io.ktor.http.URLProtocol
import kotlinx.browser.window
import kotlin.reflect.KClass

actual object Communication : CommunicationInterface {

    private val communicationImpl = CommunicationImpl(::isInternetAvailable)

    fun init(
        isProduction: Boolean,
        commProtocol: CommunicationProtocol? = null,
        companyBookApiKey: String = "",
    ) {
        communicationImpl.init(isProduction, commProtocol, companyBookApiKey)
        commLog("Communication(JS).init: isProduction=$isProduction protocol=${commProtocol != null}")
    }

    // Browsers manage TLS client certificates themselves; JavaScript/fetch cannot
    // present a client certificate programmatically. The web client therefore uses
    // plain HTTPS — the backend must allow the web origin without requiring mTLS.
    override fun getMtlsHttpClient(isProduction: Boolean, timeout: Long, token: String): HttpClient =
        getHttpClient(isProduction, timeout, token)

    override fun getHttpClient(isProduction: Boolean, timeout: Long, token: String): HttpClient = HttpClient(Js) {
        install(HttpTimeout) {
            requestTimeoutMillis = timeout
        }
        // Same-origin: the browser calls "<origin>/api/..." which a reverse proxy
        // (nginx in prod, webpack devServer locally) forwards to the real backend.
        // Keeps requests same-origin → no CORS preflight (which fails as
        // "Failed to fetch" on some mobile browsers when hitting api.mystybag.com directly).
        val originPort = window.location.port.toIntOrNull()
        configure(
            logger = object : Logger {
                override fun log(message: String) {
                    if (!isProduction) commLog(message)
                }
            },
            token = token,
            host = window.location.hostname,
            protocol = if (window.location.protocol == "https:") URLProtocol.HTTPS else URLProtocol.HTTP,
            port = originPort,
            basePath = "/api/",
        )
    }

    override var config: MystyBagSDKConfig
        get() = communicationImpl.config
        set(value) { communicationImpl.config = value }

    override var isLoginSuccess: Boolean
        get() = communicationImpl.isLoginSuccess
        set(value) { communicationImpl.isLoginSuccess = value }

    override var token: String
        get() = communicationImpl.token
        set(value) { communicationImpl.token = value }

    override fun setOnUnauthorized(handler: (suspend () -> Boolean)?) {
        communicationImpl.setOnUnauthorized(handler)
    }

    override suspend fun <Request : BaseRequestModel, Success : BaseResponseModel, Error : BaseResponseModel> sendCommand(
        request: Request,
        requestClass: KClass<Request>,
        successClass: KClass<Success>,
        errorClass: KClass<Error>
    ) = communicationImpl.sendCommand(request, requestClass, successClass, errorClass)

    override suspend fun uploadFile(target: com.example.mystybag.communication.defines.FileUploadTarget, fileData: ByteArray, fileName: String) = communicationImpl.uploadFile(target, fileData, fileName)

    private fun isInternetAvailable(): Boolean = true
}
